import { Link } from "react-router-dom";
import { Product, CATEGORY_LABEL } from "@/mocks/products";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const loc = useLocalized();
  const b = useBilingual();

  const category = CATEGORY_LABEL[product.category]
    ? loc(CATEGORY_LABEL[product.category])
    : product.category;
  const material = loc(product.material);

  return (
    <Link
      to={`/products/${product.id}`}
      className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:border-stone-300 hover:shadow-md transition-all duration-300 cursor-pointer flex flex-col"
    >
      {/* Image — fixed 4:3 card so cards stay uniform. Products are shot in mixed
          orientations (portrait chairs, landscape tables, ultra-wide TV units up
          to 3:1) on a seamless white studio background. object-cover would clip
          whichever axis overflows — wide tables lose their left/right edges, TV
          cabinets lose most of their width. Always object-contain on the same
          white field so the full product is visible and never cropped; because
          the source backgrounds are already white, the letterbox is invisible
          and the product reads as floating. */}
      <div className="relative overflow-hidden aspect-[4/3] bg-white">
        <img
          src={product.images[0]}
          alt={loc(product.name)}
          className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute bottom-3 left-3 bg-white/95 backdrop-blur text-stone-900 text-[10px] font-mono tracking-wider px-2.5 py-1 rounded-full ring-1 ring-stone-200">
          {product.id}
        </span>
        {product.badge && (
          <span
            className={`absolute top-3 right-3 text-[10px] font-semibold px-3 py-1 rounded-full ${
              product.badge === "Best Seller"
                ? "bg-amber-700 text-white"
                : product.badge === "New Arrival"
                ? "bg-stone-900 text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {product.badge}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-4 md:p-5 flex flex-col gap-2 flex-1">
        <p className="text-amber-700 text-[11px] font-semibold tracking-wider uppercase">
          {category}
        </p>
        <h3 className="text-stone-900 font-semibold text-base md:text-[17px] leading-snug line-clamp-2">
          {loc(product.name)}
        </h3>
        <p className="text-stone-500 text-xs leading-relaxed mt-auto pt-2 line-clamp-2">
          {material}
        </p>
        <div className="flex items-center justify-end pt-3 border-t border-stone-100">
          <span className="text-amber-700 text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            {b("Details", "詳情")}
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
